import { KernAlert, KernButton, KernLoader } from '@kern-ux-annex/kern-react-kit'
import type { UseQueryResult } from '@tanstack/react-query'
import { AnimatePresence, motion } from 'motion/react'
import { createContext, use, useCallback, useState, type ReactNode } from 'react'

import { ApiError } from '../api/client'

export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message
  if (error instanceof Error) return error.message
  return 'Unbekannter Fehler.'
}

const FIELD_LABELS: Record<string, string> = {
  name: 'Name',
  title: 'Titel',
  description: 'Beschreibung',
  department: 'Fachamt',
  departmentId: 'Fachamt',
  responsible: 'Verantwortlich',
  totalVolume: 'Gesamtvolumen',
  potentialGrant: 'Zuwendungshöhe',
  topicIds: 'Themenfelder',
  ratings: 'Bewertung',
  points: 'Punkte',
  amount: 'Betrag',
  fiscalYear: 'Haushaltsjahr',
  budgetEntries: 'Haushaltsansatz',
  email: 'E-Mail',
  firstName: 'Vorname',
  lastName: 'Nachname',
  slug: 'Kennung',
  logoUrl: 'Logo-Adresse',
  websiteUrl: 'Website',
  databaseUrl: 'Förderdatenbank',
  sortOrder: 'Reihenfolge',
  customWeight: 'Gewichtung',
  scaleMeanings: 'Punktebedeutung',
  criteria: 'Kriterium',
  exclusionReason: 'Ausschlussgrund',
  exclusionNote: 'Begründung',
  implementationStartYear: 'Umsetzungszeitraum (von)',
  implementationEndYear: 'Umsetzungszeitraum (bis)',
  implementationNote: 'Hinweis zum Umsetzungszeitraum',
  deadline: 'Antragsfrist',
  deadlineNote: 'Hinweis zur Antragsfrist',
  termStart: 'Programmlaufzeit (Beginn)',
  termEnd: 'Programmlaufzeit (Ende)',
  termNote: 'Hinweis zur Programmlaufzeit',
  fundingLevels: 'Förderebene',
  contacts: 'Ansprechpersonen',
  contactNote: 'Hinweis zu den Ansprechpersonen',
  grantTerms: 'Förderquote',
  cumulationKind: 'Kumulierung',
  combinableWith: 'kombinierbar mit',
  applicantTypes: 'Antragsberechtigte',
  bodyType: 'Körperschaftsart',
  version: 'Version',
}

/** "budgetEntries[1].amount" → "Haushaltsansatz 2 – Betrag" */
export function fieldLabel(path: string): string {
  return path
    .split('.')
    .map((part) => {
      const match = /^([^[]+)(?:\[(\d+)\])?$/.exec(part)
      const name = match?.[1]
      if (!match || name === undefined) return part
      const label = FIELD_LABELS[name] ?? name
      return match[2] !== undefined ? `${label} ${Number(match[2]) + 1}` : label
    })
    .join(' – ')
}

/**
 * Error box with the concrete case: the API message, every affected input by name and – for
 * server errors – the request ID to quote when reporting. For a version conflict (someone else
 * saved in the meantime) it offers "Neu laden" when ``onReload`` is given.
 */
export function ErrorAlert({
  error,
  title = 'Das hat nicht geklappt',
  onReload,
}: {
  error: unknown
  title?: string
  onReload?: () => void
}) {
  if (!error) return null
  const api = error instanceof ApiError ? error : undefined
  const conflict = Boolean(api?.isConflict)
  const issues = api?.issues.filter((issue) => issue.field) ?? []
  return (
    <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} role="alert" className="flex flex-col gap-8">
      <KernAlert title={conflict ? 'Zwischenzeitlich geändert' : title} variant={conflict ? 'warning' : 'danger'}>
        <span className="flex flex-col gap-8">
          <span>{errorMessage(error)}</span>
          {issues.length > 1 && (
            <ul className="list-disc pl-24">
              {issues.map((issue, i) => (
                <li key={`${issue.field}-${i}`}>
                  <span className="font-semibold">{fieldLabel(issue.field ?? '')}:</span> {issue.message}
                </li>
              ))}
            </ul>
          )}
          {api?.requestId && api.isServerError && (
            <span className="kern-body--small dms-muted">
              Fehler-ID für Rückfragen: <code className="select-all">{api.requestId}</code>
            </span>
          )}
        </span>
      </KernAlert>
      {conflict && onReload && (
        <div>
          <KernButton variant="secondary" icon="autorenew" label="Neu laden (eigene Änderungen verwerfen)" onClick={onReload} />
        </div>
      )}
    </motion.div>
  )
}

export function Loading({ label = 'Wird geladen …' }: { label?: string }) {
  return (
    <div className="flex items-center gap-12 py-32" role="status" aria-live="polite">
      <KernLoader />
      <span className="dms-muted">{label}</span>
    </div>
  )
}

/** Renders loading / error states of a query and the children once data is there. */
export function QueryBoundary<T>({
  query,
  children,
}: {
  query: UseQueryResult<T>
  children: (data: T) => ReactNode
}) {
  if (query.isPending) return <Loading />
  if (query.isError)
    return (
      <div className="flex flex-col gap-12">
        <ErrorAlert error={query.error} title="Daten konnten nicht geladen werden" />
        <div>
          <KernButton variant="secondary" label="Erneut versuchen" onClick={() => void query.refetch()} />
        </div>
      </div>
    )
  return <>{children(query.data)}</>
}

export function EmptyState({ title, children, action }: { title: string; children?: ReactNode; action?: ReactNode }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="dms-panel flex flex-col items-start gap-12 p-24"
    >
      <p className="kern-title">{title}</p>
      {children && <div className="dms-muted">{children}</div>}
      {action}
    </motion.div>
  )
}

// --- toasts: short confirmation after saving ------------------------------------------------

type Toast = { id: number; text: string; tone: 'success' | 'info' }
const ToastContext = createContext<(text: string, tone?: Toast['tone']) => void>(() => {})

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])
  const push = useCallback((text: string, tone: Toast['tone'] = 'success') => {
    const id = Date.now() + Math.random()
    setToasts((all) => [...all, { id, text, tone }])
    window.setTimeout(() => setToasts((all) => all.filter((t) => t.id !== id)), 3200)
  }, [])
  return (
    <ToastContext value={push}>
      {children}
      <div
        className="no-print pointer-events-none fixed right-24 bottom-24 z-50 flex flex-col items-end gap-8"
        aria-live="polite"
      >
        <AnimatePresence>
          {toasts.map((t) => (
            <motion.div
              key={t.id}
              layout
              initial={{ opacity: 0, y: 16, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, x: 24 }}
              transition={{ type: 'spring', stiffness: 420, damping: 32 }}
              className={`kern-badge kern-badge--${t.tone} pointer-events-none px-16 py-8`}
              role="status"
            >
              {t.text}
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </ToastContext>
  )
}

export const useToast = () => use(ToastContext)
